/**
 * How far through a game is, and what the bet is on track to finish at.
 *
 * The live panel could say a bet was on 30 points against a line of 47.5 and
 * not say whether that was after one quarter or three - which is the whole
 * difference between comfortably winning and comfortably losing.
 *
 * Two numbers fix that: where the clock is, and where the current rate would
 * land. The projection is deliberately the simplest one that can be checked
 * against the screen - value divided by the fraction of the game played - and
 * not a model. Scoring is not linear across an NFL game and nobody should
 * read this as a forecast; it is a way of seeing a number in proportion to
 * the time that produced it.
 */

/** Regulation length per period, in seconds. */
const PERIOD_SECONDS: Readonly<Record<string, number>> = {
  NFL: 15 * 60,
  NBA: 12 * 60,
  NHL: 20 * 60,
};

/** Periods in a regulation game. */
const REGULATION_PERIODS: Readonly<Record<string, number>> = {
  NFL: 4,
  NBA: 4,
  NHL: 3,
};

export interface GameClock {
  readonly sport: string;
  /** Period in play, 1-based. Null before the start. */
  readonly period: number | null;
  /** Seconds left in that period. */
  readonly clockSeconds: number | null;
}

/**
 * "Q3 07:12", or "OT 02:40" once regulation is done.
 *
 * Null rather than a placeholder when the feed has not given a period: an
 * empty slot in a live panel reads as missing data, which is worse than not
 * offering the reading at all.
 */
export function clockLabel(clock: GameClock): string | null {
  const { period } = clock;
  if (period === null || period < 1) return null;
  const regulation = REGULATION_PERIODS[clock.sport] ?? 4;
  const name =
    period <= regulation
      ? `${clock.sport === "NHL" ? "P" : "Q"}${String(period)}`
      : period === regulation + 1
        ? "OT"
        : `OT${String(period - regulation)}`;
  if (clock.clockSeconds === null) return name;
  // 0:00 is a break, not a frozen clock: say which one.
  if (clock.clockSeconds === 0)
    return clock.sport !== "NHL" && period === 2 ? "Half" : `End ${name}`;
  const minutes = Math.floor(clock.clockSeconds / 60);
  const seconds = clock.clockSeconds % 60;
  return `${name} ${String(minutes)}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Fraction of regulation played, 0 to 1.
 *
 * Overtime clamps to 1 rather than going past it. A game in overtime has had
 * all the time it was expected to have, so the projection there is simply the
 * value itself - which is the honest answer, not an extrapolation of a game
 * that is already longer than the model behind the line.
 */
export function fractionElapsed(clock: GameClock): number | null {
  const { period } = clock;
  if (period === null || period < 1) return null;
  const periodSeconds = PERIOD_SECONDS[clock.sport];
  const regulation = REGULATION_PERIODS[clock.sport];
  if (periodSeconds === undefined || regulation === undefined) return null;
  // A missing clock still tells us the period is under way; count it as the
  // period's start rather than throwing the reading away.
  const remaining = clock.clockSeconds ?? periodSeconds;
  const played = (period - 1) * periodSeconds + (periodSeconds - remaining);
  return Math.min(1, Math.max(0, played / (regulation * periodSeconds)));
}

/**
 * Where the current rate lands by full time, rounded to a whole number.
 *
 * Null until enough of the game has gone for the number to mean anything.
 * Early on the divisor is tiny and the projection swings wildly - one score in
 * the first three minutes projects a sixty-point quarter - so a floor is not
 * cosmetic, it stops the panel showing a number that is certain to be wrong.
 */
export const MINIMUM_ELAPSED_FOR_PROJECTION = 0.1;

export function projectedFinal(
  clock: GameClock,
  actualValue: number | null,
): number | null {
  if (actualValue === null || !Number.isFinite(actualValue)) return null;
  const elapsed = fractionElapsed(clock);
  if (elapsed === null || elapsed < MINIMUM_ELAPSED_FOR_PROJECTION) return null;
  return Math.round(actualValue / elapsed);
}

/**
 * Whether the projection is on the winning side of the line.
 *
 * Only for markets with a side and a line; returns null otherwise, because a
 * margin on a head-to-head has no line to sit above or below.
 */
export function projectionBeatsLine(
  projected: number | null,
  line: number | null,
  selection: string,
): boolean | null {
  if (projected === null || line === null) return null;
  if (selection === "OVER") return projected > line;
  if (selection === "UNDER") return projected < line;
  return null;
}

/**
 * How much of the line is left to play with.
 *
 * Measured, not modelled, and that is the point. Doubling a half-time NFL
 * score is very nearly unbiased - across 379 finished games it runs one point
 * high - but the spread around it is 13.4 points, and only a fifth of games
 * land within three of it. So the projection answers "what pace is this?"
 * honestly and "will this bet win?" barely at all.
 *
 * The room does answer it, exactly: an Under at 48.5 sitting on 21 has 27.5
 * points of room, and no model is involved. For reference, the average NFL
 * second half is about 22 points.
 */
export function roomToLine(
  actualValue: number | null,
  line: number | null,
  selection: string,
): { readonly points: number; readonly phrase: string } | null {
  if (actualValue === null || line === null) return null;
  if (selection === "UNDER") {
    const room = line - actualValue;
    return room >= 0
      ? { points: room, phrase: "to spare" }
      : { points: -room, phrase: "over the line" };
  }
  if (selection === "OVER") {
    const needed = line - actualValue;
    return needed > 0
      ? { points: needed, phrase: "still needed" }
      : { points: -needed, phrase: "past the line" };
  }
  return null;
}
